import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useApi } from '../../hooks/useApi';
import { Card, EmptyState, Loading, Notice, PersonCard, Spinner, StatusPill, Tabs } from '../../components/ui';
import JourneyRail from '../../components/JourneyRail';
import RatingReportView from '../../components/RatingReportView';
import DraftReportEditor from '../../components/DraftReportEditor';
import ConfirmDialog from '../../components/ConfirmDialog';
import DocumentsWorkspace from '../../components/DocumentsWorkspace';
import Select from '../../components/Select';
import PartnerLeads, { type Lead } from '../../components/PartnerLeads';
import PartnerSendPanel from '../../components/PartnerSendPanel';
import FundingPanel from '../../components/FundingPanel';
import RatingFeePanel from '../../components/RatingFeePanel';
import { errorMessage } from '../../lib/http';
import {
  COMPANY_STATUS,
  REPORT_STATUS,
  TRACK_TONE,
  companyDisplayName,
  flagEmoji,
  formatDate,
  formatDateTime,
  formatMoney,
  PAYMENT_STATUS,
} from '../../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE, FIELD, FIELD_STYLE, LABEL } from '../../lib/ui';
import type {
  Company,
  CompanyDocument,
  FeeSchedule,
  FundingRecord,
  Message,
  Payment,
  PersonRef,
  RatingReport,
  ReadinessBrief,
  RemediationItem,
  Requirement,
} from '../../types/workspace';
import CompanyLogo from '../../components/CompanyLogo';

type Detail = {
  company: Company;
  requirements: Requirement[];
  documents: CompanyDocument[];
  missing: Requirement[];
  reports: RatingReport[];
  remediation: RemediationItem[];
  messages: Message[];
  submissions: Lead[];
  admins: PersonRef[];
  aiMessages: number;
  brief: ReadinessBrief | null;
  payments: Payment[];
  fundings: FundingRecord[];
  feeSchedule: FeeSchedule | null;
};

type Tab = 'overview' | 'documents' | 'rating' | 'partners' | 'funding';
type Partner = { id: string; name: string; email: string; firm: string | null; focus: string | null; status: string };

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{label}</p>
      <p className="mt-0.5 text-sm text-[#4a1a30]">{value || '—'}</p>
    </div>
  );
}

export default function CompanyDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const tab: Tab =
    rawTab === 'remediation' || rawTab === 'ready'
      ? rawTab === 'ready'
        ? 'partners'
        : 'rating'
      : rawTab === 'documents' || rawTab === 'rating' || rawTab === 'partners' || rawTab === 'funding'
        ? rawTab
        : 'overview';
  const [polling, setPolling] = useState(false);
  const { data, loading, error, reload } = useApi<Detail>(`/admin/companies/${id}`, polling ? 5000 : undefined);
  const [notice, setNotice] = useState<{ tone: 'green' | 'rose'; text: string } | null>(null);
  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [partners, setPartners] = useState<Partner[]>([]);

  const busyDocs = (data?.documents || []).some((d) => d.status === 'VERIFYING' || d.status === 'UPLOADED');
  useEffect(() => setPolling(busyDocs), [busyDocs]);

  /** Partners / Funded unlock only after the customer confirms partner sharing. */
  const unlocked = Boolean(
    data?.reports?.some((r) => r.status === 'APPROVED') || data?.brief?.customerConfirmedAt,
  );

  useEffect(() => {
    if (tab !== 'partners' || !unlocked) return;
    void api.get('/admin/partners').then(({ data: d }) => setPartners(d.partners || []));
  }, [tab, unlocked]);

  useEffect(() => {
    if ((tab === 'partners' || tab === 'funding') && data && !unlocked) {
      setParams({}, { replace: true });
    }
  }, [tab, unlocked, data, setParams]);

  if (loading) return <Loading />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  const c = data.company;
  const status = COMPANY_STATUS[c.status] || { label: c.status, tone: 'slate' as const, hint: '' };
  const draft = data.reports.find((r) => r.status === 'DRAFT') || null;
  const published = data.reports.filter((r) => r.status !== 'DRAFT');
  const latestPublished = published[0] || null;
  const approvedReport = data.reports.find((r) => r.status === 'APPROVED') || null;
  const draftItems = draft ? data.remediation.filter((r) => r.report?.id === draft.id) : [];
  const cls = c.classification;
  const ratingFee = data.payments.find((p) => p.kind === 'RATING_FEE') || null;
  const feeDue = Boolean(ratingFee && ratingFee.status !== 'PAID');
  const brief = data.brief;

  const setTab = (t: Tab) => {
    if ((t === 'partners' || t === 'funding') && !unlocked) return;
    setParams(t === 'overview' ? {} : { tab: t }, { replace: true });
  };
  const flash = (tone: 'green' | 'rose', text: string) => setNotice({ tone, text });
  const onError = (text: string) => flash('rose', text);
  const refresh = () => void reload(true);

  const assign = async (adminId: string) => {
    try {
      await api.patch(`/admin/companies/${c.id}/assign`, { adminId });
      flash('green', 'Assigned analyst updated.');
      refresh();
    } catch (err) {
      onError(errorMessage(err));
    }
  };

  const setStatus = async (next: string) => {
    try {
      await api.patch(`/admin/companies/${c.id}/status`, { status: next });
      refresh();
    } catch (err) {
      onError(errorMessage(err));
    }
  };

  const setDemo = async (isDemo: boolean) => {
    try {
      await api.patch(`/admin/companies/${c.id}/demo`, { isDemo });
      flash('green', isDemo ? 'Marked as DEMO workspace.' : 'Demo flag cleared.');
      refresh();
    } catch (err) {
      onError(errorMessage(err));
    }
  };

  const generate = async () => {
    setRating(true);
    setConfirmRegen(false);
    setNotice(null);
    try {
      const { data: res } = await api.post(
        `/admin/companies/${c.id}/rating`,
        { notes: notes || null },
        { timeout: 240000 },
      );
      flash('green', `Draft v${res.report.version} ready: ${res.report.grade} (${res.report.score}/100). Review it, then publish to the customer.`);
      setNotes('');
      refresh();
    } catch (err) {
      onError(errorMessage(err, 'Rating generation failed.'));
    } finally {
      setRating(false);
    }
  };

  const requestGenerate = () => {
    if (draft) setConfirmRegen(true);
    else void generate();
  };

  const address = [c.addressLine, c.addressLine2, c.city, c.stateName, c.postalCode].filter(Boolean).join(', ');
  const activeTab: Tab = (tab === 'partners' || tab === 'funding') && !unlocked ? 'overview' : tab;

  return (
    <div>
      <Link to="/companies" className="text-xs font-bold text-[#d6006e]">
        ← All companies
      </Link>
      <div className="mz-fade-up mt-2 flex flex-wrap items-end justify-between gap-4 border-b border-[#fce7f3] pb-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#d6006e]">
            {flagEmoji(c.countryCode)} {c.countryCode}
            {c.sector ? ` · ${c.sector}` : ''}
            {c.workspaceLabel ? ` · ${c.workspaceLabel}` : ''}
          </p>
          <div className="mt-1 flex items-center gap-3">
            <CompanyLogo companyId={c.id} name={c.logicalName || c.name} hasLogo={c.hasLogo} className="h-12 w-12 rounded-xl" />
            <h1 className="min-w-0 truncate text-3xl font-black tracking-tight text-[#6b0030]">{c.logicalName || c.name}</h1>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill label={status.label} tone={status.tone} />
            {c.isDemo && <StatusPill label="DEMO — NOT A REAL RATING" tone="rose" />}
            {cls ? (
              <StatusPill label={`${cls.type.label} · ${cls.track.label} track`} tone={TRACK_TONE[cls.track.code]} />
            ) : (
              <StatusPill label="Company type not set" tone="rose" />
            )}
            {latestPublished && <StatusPill label={`v${latestPublished.version} · ${REPORT_STATUS[latestPublished.status].label}`} tone={REPORT_STATUS[latestPublished.status].tone} />}
            {draft && <StatusPill label={`Draft v${draft.version}`} tone="slate" />}
            {ratingFee && (
              <StatusPill
                label={`Rating fee ${formatMoney(ratingFee.amount, ratingFee.currency)} · ${PAYMENT_STATUS[ratingFee.status]?.label || ratingFee.status}`}
                tone={PAYMENT_STATUS[ratingFee.status]?.tone || 'slate'}
              />
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-52">
            <label className={LABEL}>Assigned analyst</label>
            <Select
              size="sm"
              value={c.assignedAdmin?.id || ''}
              onChange={(v) => void assign(v)}
              placeholder="Unassigned"
              options={data.admins.map((a) => ({ value: a.id, label: a.name || a.email, description: a.name ? a.email : undefined }))}
              aria-label="Assigned analyst"
            />
          </div>
          <div className="w-48">
            <label className={LABEL}>Status</label>
            <Select
              size="sm"
              value={c.status}
              onChange={(v) => void setStatus(v)}
              options={Object.entries(COMPANY_STATUS)
                .filter(([k]) => !['INVITED', 'REANALYSIS', 'READY'].includes(k) || k === c.status)
                .map(([k, v]) => ({ value: k, label: v.label, description: v.hint, disabled: ['INVITED', 'REANALYSIS', 'READY'].includes(k) }))}
              aria-label="Company status"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <JourneyRail
          status={c.status}
          viewingTab={activeTab}
          lockedTabs={unlocked ? undefined : ['partners', 'funding']}
          onStepClick={setTab}
        />
        <Tabs
          value={activeTab}
          onChange={setTab}
          options={[
            { id: 'overview', label: 'Overview' },
            { id: 'documents', label: 'Documents', count: data.documents.length },
            { id: 'rating', label: 'Rated', count: data.reports.length },
            { id: 'partners', label: 'Partners', count: unlocked ? data.submissions.length : undefined, disabled: !unlocked },
            { id: 'funding', label: 'Funded', count: unlocked && data.fundings.length ? data.fundings.length : undefined, disabled: !unlocked },
          ]}
        />
      </div>

      {notice && (
        <div className="mt-4">
          <Notice tone={notice.tone}>{notice.text}</Notice>
        </div>
      )}

      {!unlocked && (
        <div
          className="mt-4 rounded-2xl px-4 py-4 text-sm"
          style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46' }}
          role="status"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
            {latestPublished ? 'Step 3 · Rated · Done' : 'Waiting on Rated'}
          </p>
          <p className="mt-1 text-base font-black text-[#064e3b]">
            {latestPublished
              ? 'Waiting for the customer to confirm partner sharing'
              : 'Publish a rating first, then wait for customer confirmation'}
          </p>
          <p className="mt-1 text-[#047857]">
            Partners and Funded unlock after the customer approves the Ready pack in musb.
          </p>
        </div>
      )}

      {unlocked && activeTab !== 'partners' && data.submissions.length === 0 && (
        <div className="mt-4">
          <Notice tone="green">
            Customer approved sharing. Open{' '}
            <button type="button" onClick={() => setTab('partners')} className="font-bold underline">
              Partners
            </button>{' '}
            to send the cold email pack to funding partners.
          </Notice>
        </div>
      )}

      <div className="mt-5">
        {activeTab === 'overview' && (
          <div className="space-y-5">
            <div className="grid gap-5 lg:grid-cols-[1.4fr_0.6fr]">
              <Card title="Company profile">
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Legal name" value={c.legalName} />
                  <Field label="Entity type" value={cls?.type.label} />
                  <Field label="Rating track" value={cls?.track.label} />
                  <Field label="Registration no." value={c.registrationNumber} />
                  <Field label="Founded" value={c.foundedYear} />
                  <Field label="Employees" value={c.employeeCount} />
                  <Field label="Email" value={c.email} />
                  <Field label="Phone" value={c.phone} />
                  <Field label="Website" value={c.website} />
                  <Field label="Annual revenue" value={c.annualRevenue} />
                  <Field label="Funding ask" value={c.fundingAmount} />
                  <Field label="Funding currency" value={c.fundingCurrency} />
                  <Field label="Instrument" value={c.fundingInstrument} />
                  <Field label="Tenure (months)" value={c.fundingTenureMonths} />
                  <Field label="Target lender" value={c.targetLender} />
                  <Field label="Use of funds" value={c.fundingPurpose} />
                  <Field label="Repayment plan" value={c.fundingRepaymentPlan} />
                  <Field label="Security" value={c.fundingSecurity} />
                  <Field label="Submitted" value={formatDate(c.submittedAt)} />
                  <Field label="Address source" value={c.addressSource === 'YEABE' ? 'Yeabe (verified)' : c.addressSource === 'MANUAL' ? 'Manual' : null} />
                  <Field label="Workspace mode" value={c.isDemo ? 'DEMO — NOT A REAL RATING' : 'Live'} />
                </div>
                <div className="mt-4 border-t border-[#fce7f3] pt-3">
                  <button
                    type="button"
                    onClick={() => void setDemo(!c.isDemo)}
                    className={BTN_SOFT}
                    style={BTN_SOFT_STYLE}
                  >
                    {c.isDemo ? 'Clear demo flag (treat as live)' : 'Mark as DEMO workspace'}
                  </button>
                </div>
                {address && (
                  <p className="mt-4 border-t border-[#fce7f3] pt-3 text-sm text-[#4a1a30]">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Registered address · </span>
                    {address}
                  </p>
                )}
                {c.description && <p className="mt-3 text-sm leading-relaxed text-[#4a1a30]">{c.description}</p>}
              </Card>
              <div className="space-y-3">
                <PersonCard person={c.customer} label="Customer contact" empty="Customer has not signed in yet" />
                <PersonCard person={c.assignedAdmin} label="Assigned analyst" />
                <Card title="Quick actions">
                  <div className="flex flex-col gap-2">
                    <button type="button" onClick={() => setTab('documents')} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
                      Review {data.documents.length} document(s) · {data.missing.length} missing
                    </button>
                    <button type="button" onClick={() => setTab('rating')} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
                      {draft ? `Review draft v${draft.version}` : 'Generate rating draft'}
                    </button>
                    <button type="button" onClick={() => setTab('partners')} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
                      Partner outreach
                    </button>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <DocumentsWorkspace
            companyId={c.id}
            requirements={data.requirements}
            documents={data.documents}
            missing={data.missing}
            onChanged={refresh}
            onError={onError}
          />
        )}

        {activeTab === 'rating' && (
          <div className="space-y-5">
            {ratingFee && (
              <RatingFeePanel
                companyId={c.id}
                fee={ratingFee}
                publishedVersion={latestPublished?.version}
                hasDraft={Boolean(draft)}
                onChanged={(m) => {
                  if (m) flash('green', m);
                  refresh();
                }}
                onError={onError}
                onContinue={() => {
                  const el = document.getElementById('rating-workspace');
                  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              />
            )}

            {c.status === 'REANALYSIS' && (
              <Notice tone="pink">
                Updated documents were submitted for review{c.reanalysisRequestedAt ? ` on ${formatDateTime(c.reanalysisRequestedAt)}` : ''}. Generate and publish a refreshed rating.
              </Notice>
            )}

            <div id="rating-workspace" className={`grid gap-5 lg:grid-cols-[0.8fr_1.2fr] ${feeDue && latestPublished ? 'opacity-95' : ''}`}>
              <Card
                title={draft ? 'Regenerate draft' : 'Generate rating draft'}
                subtitle={`Uses the ${c.countryCode} prompt and the ${cls?.track.label || 'selected'} methodology with verified documents. Drafts stay in Consult until you publish.`}
                className="lg:sticky lg:top-24 lg:self-start"
              >
                {!cls && (
                  <div className="mb-3">
                    <Notice tone="rose">The company has no valid company type — ask them to set it so the right rating track is applied.</Notice>
                  </div>
                )}
                {data.missing.length > 0 && (
                  <div className="mb-3">
                    <Notice tone="amber">{data.missing.length} required document(s) missing — ask the company to upload them before publishing.</Notice>
                  </div>
                )}
                {data.missing.length === 0 && cls && !busyDocs && (
                  <div className="mb-3">
                    <Notice tone="green">Required documents are ready. Generate the rating for analyst review.</Notice>
                  </div>
                )}
                {busyDocs && (
                  <div className="mb-3">
                    <Notice tone="pink">Documents are still being verified by AI. Wait for them to finish for the best result.</Notice>
                  </div>
                )}
                <label className={LABEL}>Analyst notes (optional)</label>
                <textarea
                  rows={5}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Context from calls, bank references, sector outlook…"
                  className={`${FIELD} resize-y`}
                  style={FIELD_STYLE}
                />
                <button type="button" disabled={rating || busyDocs || data.missing.length > 0 || !cls} onClick={requestGenerate} className={`${BTN_PRIMARY} mt-4 w-full py-3`} style={BTN_PRIMARY_STYLE}>
                  {rating ? (
                    <>
                      <Spinner /> AI is analysing the file…
                    </>
                  ) : draft ? (
                    `Regenerate draft v${draft.version}`
                  ) : (
                    `Generate draft v${(data.reports[0]?.version || 0) + 1}`
                  )}
                </button>
                <p className="mt-2 text-[11px] text-[#c084a0]">
                  {ratingFee?.status === 'PAID'
                    ? 'Takes 20–60 seconds. Fee is already paid — publish this draft to move the company to Step 3 (Rated) and unlock the result in Musb.'
                    : 'Takes 20–60 seconds. After publish, the company pays the rating fee on step 3, then can replace documents and resubmit for review.'}
                </p>
              </Card>
              <div className="space-y-4">
                {draft && (
                  <DraftReportEditor
                    key={`${draft.id}-${draft.version}`}
                    companyId={c.id}
                    companyName={companyDisplayName(c)}
                    report={draft}
                    items={draftItems}
                    onChanged={(m) => {
                      if (m) flash('green', m);
                      refresh();
                    }}
                    onError={onError}
                  />
                )}
                {published.length === 0 && !draft ? (
                  <EmptyState title="No rating yet" body="Generate the first draft once the company has submitted its documents." />
                ) : (
                  published.map((r, i) => (
                    <div key={r.id} className="space-y-2">
                      {r.status === 'APPROVED' && <p className="text-[11px] font-semibold text-[#047857]">Ready summary agreed on this version — cleared for partner outreach.</p>}
                      <RatingReportView report={r} defaultOpen={i === 0 && !draft} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'partners' && unlocked && (approvedReport || latestPublished) && (
          <div className="space-y-5">
            <PartnerSendPanel
              companyId={c.id}
              partners={partners}
              leads={data.submissions}
              documents={data.documents}
              requirements={data.requirements}
              reportVersion={(approvedReport || latestPublished)!.version}
              approvedAt={brief?.customerConfirmedAt}
              onSent={(m) => {
                flash('green', m);
                refresh();
              }}
              onError={onError}
            />
            <PartnerLeads
              companyId={c.id}
              leads={data.submissions}
              versionOf={(id) => data.reports.find((r) => r.id === id)?.version}
              onChanged={(m) => {
                if (m) flash('green', m);
                refresh();
              }}
              onError={onError}
            />
          </div>
        )}

        {activeTab === 'funding' && unlocked && (
          <FundingPanel
            key={c.id}
            companyId={c.id}
            fundings={data.fundings}
            payments={data.payments}
            leads={data.submissions}
            feeSchedule={data.feeSchedule}
            onChanged={(m) => {
              if (m) flash('green', m);
              refresh();
            }}
            onError={onError}
          />
        )}
      </div>

      <ConfirmDialog
        open={confirmRegen}
        title={draft ? `Replace draft v${draft.version}?` : 'Generate rating draft?'}
        description="A new AI draft will replace the current one and its remediation items. This usually takes 20–60 seconds."
        confirmLabel="Regenerate draft"
        cancelLabel="Cancel"
        busy={rating}
        onCancel={() => !rating && setConfirmRegen(false)}
        onConfirm={() => void generate()}
      />
    </div>
  );
}
